import { useCallback, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Select } from "antd";
import { ApolloError } from "@apollo/client";

import { useGetJurisdictionsQuery } from "graphql/queries/get-jurisdictions.generated";
import { useLoggedUser } from "state/reactive-vars/logged-user-var";
import { UserRoleValue } from "constants/field-values";

/**
 * Which AHJ the rules screens are working on.
 *
 * An authority user belongs to one jurisdiction and never picks: it is the one on
 * their organization. A platform admin belongs to none — their organization has no
 * jurisdiction_id at all — so without a picker these screens would query an empty id
 * and show nothing. They choose, and the choice drives the whole screen.
 */
export const usePlanReviewJurisdiction = () => {
	const { loggedUserValue } = useLoggedUser();
	const ownJurisdictionId = loggedUserValue.organization?.jurisdiction_id ?? "";
	const isPlatformAdmin = loggedUserValue.role === UserRoleValue.admin_user;

	// A link from another rules screen carries the AHJ it was looking at, so a platform
	// admin who picked one does not have to pick it again on arrival.
	const [searchParams] = useSearchParams();
	const [chosenId, setChosenId] = useState<string>(searchParams.get("jurisdiction") || ownJurisdictionId);
	const jurisdictionId = isPlatformAdmin ? chosenId : ownJurisdictionId;

	const { data, loading } = useGetJurisdictionsQuery({
		skip: !isPlatformAdmin,
		fetchPolicy: "cache-first",
		onError: useCallback((err: ApolloError) => console.error("getJurisdictions error:", err), [])
	});

	const options = useMemo(() => (data?.jurisdictions ?? []).map(j => ({ label: j.name, value: j.id })), [data]);

	// Rendered by the screen in its header; null for a user whose jurisdiction is fixed.
	const picker = isPlatformAdmin ? (
		<div className="ahj-picker-filter">
			<label className="ahj-picker-label">Jurisdiction</label>
			<Select
				className="ahj-picker-control"
				showSearch
				optionFilterProp="label"
				placeholder="Choose an AHJ to manage"
				value={jurisdictionId || undefined}
				options={options}
				onChange={setChosenId}
				loading={loading}
				style={{ minWidth: 260 }}
			/>
		</div>
	) : null;

	// What to tell the user when there is nothing to work on yet.
	const emptyReason = jurisdictionId
		? null
		: isPlatformAdmin
			? {
					title: "Choose a jurisdiction",
					detail: "Plan review rules belong to an AHJ. Pick one above to manage its rules."
				}
			: {
					title: "No jurisdiction on your organization",
					detail: "Plan review rules belong to a jurisdiction. Ask a platform admin to assign yours."
				};

	return { jurisdictionId, isPlatformAdmin, picker, emptyReason };
};
